import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Mail, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errMsg } from "../ui";
import { RunDays } from "./RunDays";
import { RunDomains } from "./RunDomains";
import { RunLog } from "./RunLog";
import { RunState, describeRun, fmtDayY, fmtTime } from "./meta";

function Header({ run }) {
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="uc-run-header">
      <Link to="/billing-sync/usage-charges" className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900" data-testid="uc-run-back"><ArrowLeft className="h-3.5 w-3.5" /> Runs</Link>
      <h3 className="font-heading text-base font-semibold text-slate-900" data-testid="uc-run-title">{describeRun(run.params)}</h3>
      <RunState run={run} />
      <span className="text-xs text-slate-500">{run.trigger === "daily" ? "Daily schedule" : run.started_by_name} · {fmtTime(run.started_at)}{run.finished_at ? ` → ${fmtTime(run.finished_at)}` : ""} · <span className="font-mono">{run.id}</span></span>
    </div>
  );
}

function Progress({ run }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-5 py-4" data-testid="uc-run-progress">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-800"><Loader2 className="h-4 w-4 animate-spin text-emerald-600" /> {run.step}
        <span className="ml-auto font-mono text-xs tabular-nums text-slate-500">{run.pct || 0}%</span></div>
      <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-500 transition-[width] duration-700" style={{ width: `${Math.max(run.pct || 0, 3)}%` }} /></div>
    </div>
  );
}

function EmailNote({ email }) {
  if (!email) return null;
  return (
    <p className="flex items-center gap-1.5 text-xs text-slate-500" data-testid="uc-run-email">
      <Mail className="h-3.5 w-3.5" /> Summary e-mail <b>{email.status}</b> {fmtTime(email.at)} to {(email.to || []).join(", ")}</p>
  );
}

/** One usage run: the days collected, the open cycle's amounts per domain, and the log. */
export function UsageRunDetail() {
  const { rid } = useParams();
  const [run, setRun] = useState(null);
  const load = useCallback(() => api.get(`/usage-charges/runs/${rid}`).then(({ data }) => setRun(data))
    .catch((e) => { toast.error(errMsg(e, "Could not load the run")); setRun(false); }), [rid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (run?.status !== "running") return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [run?.status, load]);
  if (run === null) return <Loader2 className="mx-auto mt-12 h-6 w-6 animate-spin text-slate-300" />;
  if (!run) return <p className="py-10 text-center text-sm text-slate-500" data-testid="uc-run-missing">This run no longer exists.</p>;
  return (
    <div className="space-y-4" data-testid="uc-run-detail">
      <Header run={run} />
      {run.status === "running" && <Progress run={run} />}
      {run.status === "failed" && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-800" data-testid="uc-run-error">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0" /><div><b>The run stopped.</b> {run.error}</div></div>)}
      <EmailNote email={run.email} />
      {run.days?.length > 0 && <RunDays days={run.days} checkOnly={run.params.check_only} />}
      {run.usage && run.cycle && (
        <div className="text-xs text-slate-600" data-testid="uc-run-cycle">
          <b>{run.cycle.label} cycle</b> {fmtDayY(run.cycle.start)} → {fmtDayY(run.cycle.end)}, totalled through {fmtDayY(run.cycle.through)}
          {run.cycle.empty_days?.length > 0 && <span className="ml-1 text-amber-700" data-testid="uc-run-empty-days">· no calls saved yet for {run.cycle.empty_days.length} day(s): {run.cycle.empty_days.join(", ")}</span>}
        </div>)}
      {run.usage && <RunDomains run={run} />}
      <RunLog log={run.log} />
    </div>
  );
}
